import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api/client';
import { Section, SectionEyebrow, SectionTitle } from '../components/ui/Section';
import { PillButton } from '../components/ui/PillButton';
import { TextArea } from '../components/ui/TextInput';
import { todayIso } from '../lib/format';
import { useAuth } from '../lib/auth/store';
import { MoodLineChart } from '../components/charts/MoodLineChart';
import { MOOD_RATINGS, type MoodRating } from '@mindrift/shared';

const RANGES = [7, 30, 90] as const;
const MOOD_LABELS: Record<number, string> = {
  1: 'Awful',
  2: 'Low',
  3: 'OK',
  4: 'Good',
  5: 'Great',
};

export const Route = createFileRoute('/_app/mood')({
  component: MoodPage,
});

function MoodPage() {
  const settings = useAuth((s) => s.settings);
  const qc = useQueryClient();
  const [range, setRange] = useState<typeof RANGES[number]>(30);
  const areas = ['Work', 'Personal', 'Health', 'Social', ...(settings?.customAreas ?? [])];
  const today = todayIso();

  const { data: todayData } = useQuery({
    queryKey: ['mood', 'today'],
    queryFn: () => api.mood.byDate(today),
  });

  const [rating, setRating] = useState<MoodRating | null>(null);
  const [note, setNote] = useState('');
  const [tags, setTags] = useState<string[]>([]);

  const log = useMutation({
    mutationFn: () => api.mood.log({ rating: rating!, note: note.trim() || null, tags }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mood'] });
    },
  });

  const { data: trend } = useQuery({
    queryKey: ['mood', 'trend', range],
    queryFn: () => api.mood.trend(range),
  });

  return (
    <>
      <Section surface="pale-blue">
        <SectionEyebrow>MOOD</SectionEyebrow>
        <SectionTitle size="section-heading" className="mt-3">How are you today?</SectionTitle>
        <p className="text-body-lg text-muted-slate mt-3 max-w-xl">
          Pick a number. Add a note if you want. That's it.
        </p>

        <div className="mt-10 flex flex-wrap items-end gap-3">
          {MOOD_RATINGS.map((r) => {
            const isSelected = todayData?.entry?.rating === r || rating === r;
            return (
              <button
                key={r}
                onClick={() => setRating(r)}
                className={`flex flex-col items-center gap-1.5 px-4 py-3 rounded-md border transition-all min-w-[72px] ${
                  isSelected
                    ? 'bg-coral text-canvas-white border-coral'
                    : 'bg-canvas-white border-hairline hover:border-coral'
                }`}
                aria-label={`Rate ${r} out of 5`}
                type="button"
              >
                <span className="text-card-heading font-display">{r}</span>
                <span className="text-micro">{MOOD_LABELS[r]}</span>
              </button>
            );
          })}
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextArea
            label="Note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="A short sentence about today."
          />
          <div className="flex flex-col gap-3">
            <p className="field-label">Tags</p>
            <div className="flex flex-wrap gap-2">
              {areas.map((a) => {
                const on = tags.includes(a);
                return (
                  <button
                    key={a}
                    onClick={() => setTags(on ? tags.filter((t) => t !== a) : [...tags, a])}
                    className={`pill-outline text-caption ${on ? 'bg-coral text-canvas-white border-coral' : ''}`}
                    type="button"
                  >
                    {a}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <PillButton
            onClick={() => log.mutate()}
            loading={log.isPending}
            disabled={rating === null}
          >
            {todayData?.entry ? 'Update today' : 'Log today'}
          </PillButton>
          {todayData?.entry && (
            <p className="text-caption text-muted-slate">
              You logged {todayData.entry.rating}/5 earlier.
            </p>
          )}
        </div>
      </Section>

      <Section surface="canvas">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div className="min-w-0">
            <SectionEyebrow>TREND</SectionEyebrow>
            <SectionTitle size="card-heading" className="mt-1">Mood over time</SectionTitle>
            {trend && (
              <p className="text-caption text-muted-slate mt-2">
                {trend.streak}-day streak
                {trend.byArea && Object.keys(trend.byArea).length > 0 && (
                  <span> · avg by area: {Object.entries(trend.byArea).map(([k, v]) => `${k} ${v}`).join(' · ')}</span>
                )}
              </p>
            )}
          </div>
          <div className="flex gap-1 shrink-0">
            {RANGES.map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={`pill-outline text-caption ${range === r ? 'bg-near-black text-canvas-white border-near-black' : ''}`}
                type="button"
              >
                {r}D
              </button>
            ))}
          </div>
        </div>
        {trend && <MoodLineChart data={trend.entries} />}
      </Section>
    </>
  );
}
